/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';

export default class TransactionDeleter extends Component {
  @service
  session;

  @service
  features;

  @service
  finance;

  @service
  toaster;

  transaction = null;
  TransactionType = TransactionType;

  @computed(
    'features.sapFinance',
    'transaction.{isCancelled,method,sourceTransaction.id,lineItem.isArchived}',
  )
  get canDelete() {
    if (
      this.features.sapFinance &&
      this.transaction.type === TransactionType.REFUND
    ) {
      // Refunds cannot be cancelled once sent to SAP (which happens
      // immediately).
      return false;
    }
    // Deletion/cancellation is not allowed for:
    // - child transactions (delete/cancel the source, and it will cascade),
    // - transactions whose line item is archived (i.e. fully voided),
    // - transactions for online payments (i.e. Stripe or NSW CPP),
    // - transactions which have already been cancelled.
    return !(
      this.get('transaction.sourceTransaction.id') ||
      this.get('transaction.lineItem.isArchived') ||
      this.transaction.method === PaymentMethod.ONLINE ||
      this.transaction.isCancelled
    );
  }

  @computed('transaction.{method,type}')
  get tillDisabled() {
    if (this.transaction.type !== TransactionType.PAYMENT) {
      // No need for till selection on non-payments (i.e. transfers).
      return true;
    }
    return this.finance.tillDisabled(this.transaction.method);
  }

  @computed('features.sapFinance')
  get modeLabel() {
    if (this.features.sapFinance) {
      return 'Cancel';
    }
    return 'Delete';
  }

  @computed('transaction.{method,type}')
  get showRejectedPayment() {
    if (this.transaction.type !== TransactionType.PAYMENT) {
      // Not applicable to non-payments.
      return false;
    }
    switch (this.transaction.method) {
      case PaymentMethod.CHEQUE:
      case PaymentMethod.EFTPOS:
      case PaymentMethod.ONLINE:
        return true;
    }

    return false;
  }

  @tracked
  dialogOpen = false;

  @tracked
  reason = '';

  @action
  setReason(value) {
    this.reason = value;
  }

  @tracked
  requestedBy = '';

  @action
  setRequestedBy(value) {
    this.requestedBy = value;
  }

  @tracked
  isRejectedPayment = false;

  @action
  setIsRejectedPayment(value) {
    this.isRejectedPayment = value;
  }

  @tracked
  deletionInProgress = false;

  @action
  openDialog() {
    this.dialogOpen = true;
  }

  @action
  closeDialog() {
    this.resetForm();
  }

  @action
  cancelTransaction() {
    this.deletionInProgress = true;
    this.finance
      .cancelTransaction(
        this.transaction,
        this.reason,
        this.requestedBy,
        this.isRejectedPayment,
      )
      .then(() => {
        if (!(this.isDestroyed || this.isDestroying)) {
          this.dialogOpen = false;
        }
        this.toaster.success(`Transaction cancelled successfully.`);
        if (!isEmpty(this.onDelete)) {
          this.onDelete(this.transaction);
        }
      })
      .catch(() => {
        this.toaster.error(`Failed to cancel transaction.`);
      })
      .finally(() => {
        if (!(this.isDestroyed || this.isDestroying)) {
          this.deletionInProgress = false;
        }
      });
  }

  @action
  delete() {
    this.deletionInProgress = true;
    this.transaction.set('auditReason', this.reason);
    this.transaction
      .destroyRecord()
      .then(() => {
        if (!(this.isDestroyed || this.isDestroying)) {
          this.dialogOpen = false;
        }
        this.toaster.success(`Transaction deleted successfully.`);
        if (!isEmpty(this.onDelete)) {
          this.onDelete();
        }
      })
      .catch(() => {
        this.toaster.error(`Failed to delete transaction.`);
      })
      .finally(() => {
        if (!(this.isDestroyed || this.isDestroying)) {
          this.deletionInProgress = false;
        }
      });
  }

  resetForm() {
    this.dialogOpen = false;
    this.reason = '';
    this.requestedBy = '';
    this.isRejectedPayment = false;
    this.deletionInProgress = false;
  }
}
